import { Injectable } from '@angular/core';
import {
  AssignmentFormData,
  AssignmentRequest,
  Timestamp,
  Zone,
  AlertData,
  AssignmentDriver,
  DriversAssignationModel
} from '../models';
import { SECONDS_PER_MINUTE } from '../constants';

declare const moment: any;

/**
 * Service responsible for transforming form data into API format
 * Single Responsibility: Data transformation and formatting
 */
@Injectable()
export class AssignmentTransformerService {
  /**
   * Transforms form data into assignment request format
   */
  transformToRequest(
    formData: AssignmentFormData,
    zones: Zone[],
    availableDrivers: AssignmentDriver[]
  ): AssignmentRequest {
    const zone = this.getZone(formData.zoneId!, zones);

    const selectedDrivers = Array.isArray(formData.selectedDrivers)
      ? formData.selectedDrivers
      : [];

    const selectedDriverIds = new Set(
      selectedDrivers.map((driver) => driver.driverId)
    );

    const request: AssignmentRequest = {
      zoneId: zone.zoneId,
      targetDate: this.createTimestamp(formData.targetDate),
      generalTimeMargin: this.minutesToTimestamp(formData.generalTimeMargin),
      exterProviderTimeMargin: this.minutesToTimestamp(formData.exterProviderTimeMargin),
      extraToppingTimeMargin: this.minutesToTimestamp(formData.extraToppingTimeMargin),
      maxDriverWalkTime: this.minutesToTimestamp(formData.maxDriverWalkTime),
      maxDriverPublicTransportTime: this.minutesToTimestamp(formData.maxDriverPublicTransportTime),
      sweepCarRadiusInKilometers: Number(formData.sweepCarRadiusInKilometers),
      sweepCarMaxArrivalTime: this.minutesToTimestamp(formData.sweepCarMaxArrivalTime),
    };

    request.assignedDrivers = availableDrivers.map((driver) =>
      this.mapDriverToAssignationModel(
        driver,
        selectedDriverIds.has(driver.driverId)
      )
    );

    return request;
  }

  /**
   * Creates alert data for notifications
   * For date-only values, formats in UTC to avoid timezone shifts
   */
  createAlertData(timestamp: Timestamp, zoneId: string, zones: Zone[]): AlertData {
    const zone = zones.find((z) => z.zoneId === zoneId);
    
    if (!zone) {
      throw new Error(`Zone not found: ${zoneId}`);
    }

    // For date-only values (targetDate), use UTC to avoid day shifts
    // moment.utc interprets the timestamp as UTC, preventing timezone conversion issues
    const dateInUTC = moment.utc(timestamp.seconds * 1000);

    return {
      date: dateInUTC.format('DD/MM/YYYY'),
      zone: `(${zone.timeZone})`,
      zoneName: zone.zoneName,
      unix: timestamp,
    };
  }
  private mapDriverToAssignationModel(
    driver: AssignmentDriver,
    assignDriver: boolean
  ): DriversAssignationModel {
    const isFull = driver.schedule.mode === 'full';

    return {
      driverId: driver.driverId,
      assignDriver,
      firstShiftStartTime: this.timeStringToDuration(
        isFull ? driver.schedule.full.start : driver.schedule.split.morningStart
      ),
      firstShiftEndTime: this.timeStringToDuration(
        isFull ? driver.schedule.full.end : driver.schedule.split.morningEnd
      ),
      secondShiftStartTime: this.timeStringToDuration(
        isFull ? '' : driver.schedule.split.afternoonStart
      ),
      secondShiftEndTime: this.timeStringToDuration(
        isFull ? '' : driver.schedule.split.afternoonEnd
      )
    };
  }

  private timeStringToDuration(timeString: string): { seconds: string } {
    if (!timeString || timeString.trim() === '') {
      return { seconds: '0' };
    }

    const [hours, minutes] = timeString.split(':').map(Number);
    const totalSeconds = hours * 3600 + minutes * 60;

    return { seconds: totalSeconds.toString() };
  }

  /**
   * Converts minutes to timestamp format
   */
  private minutesToTimestamp(minutes: number): Timestamp {
    return {
      seconds: minutes * SECONDS_PER_MINUTE,
    };
  }

  /**
   * Creates timestamp from date string
   * Input format: YYYY-MM-DD from HTML date input
   * Always uses UTC to ensure dates are consistent across all timezones
   */
  private createTimestamp(dateString: string): Timestamp {
    const date = moment.utc(dateString).startOf('day');
    return {
      seconds: date.unix(),
    };
  }

  /**
   * Gets zone object from zone name
   */
  private getZone(zoneName: string, zones: Zone[]): Zone {
    const selectedZone = zones.find((zone) => zone.zoneName === zoneName);
    
    if (!selectedZone) {
      throw new Error(`Zone not found: ${zoneName}`);
    }
    
    return selectedZone;
  }
}
